import { create } from 'zustand';

export type SkillDraft = {
  slug: string;
  name: string;
  description: string;
  body: string;
};

export type SkillDraftField = keyof SkillDraft;

export type SkillDraftUpdateEffect = {
  fields: SkillDraftField[];
  timestamp: number;
};

type SkillDraftsState = {
  drafts: Record<string, SkillDraft>;
  updateEffects: Record<string, SkillDraftUpdateEffect>;
  setDraft: (id: string, draft: SkillDraft) => void;
  patchDraft: (id: string, patch: Partial<SkillDraft>) => void;
  removeDraft: (id: string) => void;
  getDraft: (id: string) => SkillDraft | undefined;
};

export const EMPTY_SKILL_DRAFT: SkillDraft = {
  slug: '',
  name: '',
  description: '',
  body: '',
};

export const useSkillDraftsStore = create<SkillDraftsState>((set, get) => ({
  drafts: {},
  updateEffects: {},
  setDraft: (id, draft) => {
    set((state) => ({
      drafts: {
        ...state.drafts,
        [id]: draft,
      },
    }));
  },
  patchDraft: (id, patch) => {
    const normalizedPatch = Object.fromEntries(
      Object.entries(patch).filter(([, value]) => value !== undefined),
    ) as Partial<SkillDraft>;
    const updatedFields = Object.keys(normalizedPatch) as SkillDraftField[];
    set((state) => ({
      drafts: {
        ...state.drafts,
        [id]: {
          ...(state.drafts[id] ?? EMPTY_SKILL_DRAFT),
          ...normalizedPatch,
        },
      },
      updateEffects:
        updatedFields.length > 0
          ? {
              ...state.updateEffects,
              [id]: {
                fields: updatedFields,
                timestamp: Date.now(),
              },
            }
          : state.updateEffects,
    }));
  },
  removeDraft: (id) => {
    set((state) => {
      const { [id]: _removedDraft, ...drafts } = state.drafts;
      const { [id]: _removedEffect, ...updateEffects } = state.updateEffects;
      return { drafts, updateEffects };
    });
  },
  getDraft: (id) => get().drafts[id],
}));
